import clsx from 'clsx';

/**
 * Overview — All Applications.
 *
 * Michael Wallace, 2026-09-13: "It would also be good to add 'Overview – All Apps' as a screen
 * within the FLD. Placement could be to the right of 'Summary of Risks' and to the left of
 * 'Underwriting.'"
 *
 * The screen an analyst reads before trusting any Underwriting or Pricing disparity: it shows what
 * the LAR is actually made of. Five of the ten fields decide whether the standard CFLPRSBnotComml
 * population is appropriate for this bank, and those are marked rather than left to be remembered.
 */

interface OverviewRow {
  code: string;
  label: string;
  count: number;
  pct: number;
  isUnexpected?: boolean;
}

interface OverviewSection {
  key: string;
  label: string;
  fieldNumber: number;
  rows: OverviewRow[];
  total: number;
  determinesCflprsb: boolean;
}

export interface FldOverview {
  title: string;
  totalApplications: number;
  sections: OverviewSection[];
}

interface Props {
  overview: FldOverview | null | undefined;
  /** Filter ids active on the run, so the screen can say it is deliberately ignoring them. */
  appliedFilters?: string[];
}

export default function FldOverviewTab({ overview, appliedFilters }: Props) {
  if (!overview || overview.sections.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-ink-2 font-medium">No overview available for this analysis.</p>
        <p className="text-ink-3 text-sm mt-1">
          Re-run the analysis to generate the Overview — results saved before this feature shipped do
          not carry one.
        </p>
      </div>
    );
  }

  const filtered = (appliedFilters?.length ?? 0) > 0;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-ink">Overview — All Applications</h2>
        <p className="text-sm text-ink-3 mt-0.5">{overview.title}</p>
        <p className="text-xs text-ink-4 mt-1">
          All {overview.totalApplications.toLocaleString()} applications in the LAR.
          {filtered
            ? ' This table is deliberately NOT narrowed by the active population filter — its purpose is to evidence whether a filter is appropriate.'
            : ' No population filter applied.'}
        </p>
      </div>

      <p className="text-xs text-ink-3 bg-panel-2 border border-rule rounded-md px-3 py-2">
        Fields marked <CflprsbDot /> determine whether the standard{' '}
        <span className="font-medium text-ink-2">CFLPRSBnotComml</span> population — the basis of the
        Underwriting and Pricing tables — is appropriate for this institution.
      </p>

      {/* Michael Wallace, 2026-09-24. Same sentence as the Word and Excel exports (fldExport.ts). */}
      <p className="text-xs text-ink-3">
        Purchased loans (Action Taken 6) are shown in this Overview but are removed from every other
        table in this analysis.
      </p>

      <div className="grid gap-5 md:grid-cols-2">
        {overview.sections.map(section => (
          <SectionTable key={section.key} section={section} />
        ))}
      </div>
    </div>
  );
}

function CflprsbDot() {
  return (
    <span
      className="inline-block w-1.5 h-1.5 rounded-full bg-teal-600 align-middle mx-0.5"
      aria-label="determines CFLPRSBnotComml applicability"
    />
  );
}

function SectionTable({ section }: { section: OverviewSection }) {
  return (
    <div className="bg-panel rounded-lg border border-rule overflow-hidden">
      <div className="px-3 py-2 border-b border-rule bg-panel-2 flex items-baseline gap-2">
        <h3 className="text-xs font-semibold text-ink-2 uppercase tracking-wider">
          {section.label}
        </h3>
        {section.determinesCflprsb && <CflprsbDot />}
        <span className="text-[11px] text-ink-4 ml-auto">Field {section.fieldNumber}</span>
      </div>

      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-rule">
            <th className="text-left py-1.5 px-3 text-[11px] font-semibold text-ink-3 uppercase tracking-wider w-12">
              Code
            </th>
            <th className="text-left py-1.5 px-3 text-[11px] font-semibold text-ink-3 uppercase tracking-wider">
              HMDA Field
            </th>
            <th className="text-right py-1.5 px-3 text-[11px] font-semibold text-ink-3 uppercase tracking-wider w-16">
              #
            </th>
            <th className="text-right py-1.5 px-3 text-[11px] font-semibold text-ink-3 uppercase tracking-wider w-20">
              %
            </th>
          </tr>
        </thead>
        <tbody>
          {section.rows.map((row, i) => (
            <tr
              key={`${row.code}-${i}`}
              className={clsx(
                'border-b border-rule/60',
                // A zero row is meaningful — the bank does not do this — but it should not compete
                // for attention with the rows that carry volume.
                row.count === 0 && 'text-ink-4',
                row.isUnexpected && 'bg-amber-50',
              )}
            >
              <td className="py-1.5 px-3 tabular-nums text-ink-3">{row.code}</td>
              <td className="py-1.5 px-3 text-ink-2">
                {row.label}
                {row.isUnexpected && (
                  <span className="ml-1.5 text-[10px] text-amber-700 uppercase tracking-wide">
                    not a valid code
                  </span>
                )}
              </td>
              <td className="py-1.5 px-3 text-right tabular-nums text-ink">
                {row.count.toLocaleString()}
              </td>
              <td className="py-1.5 px-3 text-right tabular-nums text-ink-2">
                {row.pct.toFixed(2)}%
              </td>
            </tr>
          ))}
          <tr className="font-semibold bg-panel-2">
            <td className="py-1.5 px-3 text-ink-2">Total</td>
            <td className="py-1.5 px-3" />
            <td className="py-1.5 px-3 text-right tabular-nums text-ink">
              {section.total.toLocaleString()}
            </td>
            <td className="py-1.5 px-3 text-right tabular-nums text-ink-2">
              {section.total > 0 ? '100%' : '0%'}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
